import { ChatActionBar } from '@parentsquare/squarekit/molecules/chat/ChatActionBar';
import { CopyToggle } from '@parentsquare/squarekit/components/app/ToggleReaction/CopyToggle';
import { ThumbsUpToggle } from '@parentsquare/squarekit/components/app/ToggleReaction/ThumbsUpToggle';
import { ThumbsDownToggle } from '@parentsquare/squarekit/components/app/ToggleReaction/ThumbsDownToggle';
import React, { useMemo } from 'react';
import { ensureContrast } from '@/Hooks/useContrastColor';
import { useChatBotTheme } from '@/Public/ChatBot/Hooks/useChatBotTheme';
import type { Citation } from '@/Public/ChatBot/Types';

interface MessageActionsProps {
    readonly content: string;
    readonly citations?: readonly Citation[];
    readonly feedback?: 'up' | 'down';
    readonly onRequestFeedback: (direction: 'up' | 'down') => void;
    readonly isFeedbackOpen?: boolean;
}

function buildCopyText(content: string, citations?: readonly Citation[]): string {
    if (!citations?.length) return content;
    const sources = citations
        .map((c) => c.source_document_title ? `${c.source_document_title}: ${c.source_document_link}` : c.source_document_link)
        .join('\n');
    return `${content}\n\nSources:\n${sources}`;
}

export const MessageActions = ({
    content,
    citations,
    feedback,
    onRequestFeedback,
    isFeedbackOpen = false,
}: MessageActionsProps): React.ReactNode => {
    const { primaryColor } = useChatBotTheme();
    const accessibleColor = useMemo(() => ensureContrast(primaryColor, '#ffffff'), [primaryColor]);
    const copyText = buildCopyText(content, citations);

    const handleThumbsUp = (): void => {
        if (feedback !== 'up') {
            onRequestFeedback('up');
        }
    };

    const handleThumbsDown = (): void => {
        if (feedback !== 'down') {
            onRequestFeedback('down');
        }
    };

    return (
        <div style={{
            '--color-border-theme': accessibleColor,
            '--color-theme-default': accessibleColor,
            '--color-intent-on-primary-default': '#ffffff',
        } as React.CSSProperties}>
            <ChatActionBar>
                <CopyToggle copyText={copyText} tooltip="Copy message" />
                <span
                    role="group"
                    aria-label="Message feedback"
                    aria-haspopup="dialog"
                    aria-expanded={isFeedbackOpen}
                    aria-controls="feedback-drawer"
                    className="tw:inline-flex tw:items-center tw:gap-(--spacing-8)"
                >
                    <ThumbsUpToggle
                        pressed={feedback === 'up'}
                        onPressedChange={handleThumbsUp}
                        tooltip="Helpful"
                    />
                    <ThumbsDownToggle
                        pressed={feedback === 'down'}
                        onPressedChange={handleThumbsDown}
                        tooltip="Not helpful"
                    />
                </span>
            </ChatActionBar>
        </div>
    );
};
